@if (currentStep < 3) {
  <div class="onboarding">
    <div class="onboarding__illustration">
      <app-onboarding-preview
        [step]="currentStep"
        [profileForm]="profileForm"
        [serviceForm]="serviceForm"
        [serviceImage]="serviceImageFile"
        [user]="user"
      ></app-onboarding-preview>
    </div>

    <div class="onboarding__content">
      <app-onboarding-page-progress class="onboarding__content-page-progress"></app-onboarding-page-progress>

      <!-- ===== STEP 0: LOADING ===== -->
      @if (currentStep === 0) {
        <section id="onboarding-step0" class="onboarding__content-section">
          <div class="skeleton-loading onboarding__content-title"></div>
          <div class="skeleton-loading onboarding__content-subtitle"></div>

          <div class="input-group">
            <div class="input-group__label skeleton-loading"></div>
            <div class="input-group__input skeleton-loading"></div>
          </div>
        </section>
      }

      <!-- ===== STEP 1: PUBLIC PROFILE ===== -->
      @if (currentStep === 1) {
        <section id="onboarding-step1" class="onboarding__content-section">
          <div class="onboarding__content-section-content">
            <h1 class="onboarding__content-title">{{ 'onboarding.step1.title' | transloco }}</h1>
            <p class="onboarding__content-subtitle">{{ 'onboarding.step1.subtitle' | transloco }}</p>

            <!-- PROFILE FORM -->
            <form class="onboarding__content-form" [formGroup]="profileForm" (ngSubmit)="publishProfile()">
              <div class="input-group--side-by-side">
                <div class="input-group">
                  <label for="firstname">{{ 'onboarding.form.firstname' | transloco }}</label>
                  <input id="firstname" type="text" formControlName="firstname" autocomplete="given-name" />
                  @if (firstName.invalid && firstName.touched) {
                    <small class="error">{{ 'onboarding.errors.firstname' | transloco }}</small>
                  }
                </div>

                <div class="input-group">
                  <label>{{ 'onboarding.form.image' | transloco }}</label>
                  <app-image-upload
                    [buttonSize]="'medium'"
                    [shouldCompress]="false"
                    (imagesSelected)="uploadProfilePicture($event)">
                  </app-image-upload>
                </div>
              </div>

              <div class="input-group">
                <label for="biography">{{ 'onboarding.form.biography' | transloco }}</label>
                <textarea id="biography" rows="5" formControlName="biography"></textarea>
                <small class="hint">
                  <span [class.error]="profileForm.value.biography?.length > 300">{{ profileForm.value.biography?.length || 0 }}/300</span> — {{ 'onboarding.hints.bio' | transloco }}
                </small>
              </div>

              <app-city-selector formControlName="city"></app-city-selector>

              <app-tags-selector
                formControlName="tags"
                [max]="5"
                [position]="'top'"
                (maxReached)="showErrorToast('public-profile.identity.tags.error.max-5')">
              </app-tags-selector>
            </form>
          </div>
          <div class="onboarding__content-section-actions">
            <div></div>
            <div class="onboarding__content-section-actions-group">
              @if (profileForm.valid) {
                <p class="ready">
                  <i class="fa-solid fa-circle-check"></i>
                  {{ 'onboarding.public-profile-ready' | transloco }}
                </p>
              }
              <button class="button"
                      type="submit"
                      [disabled]="savingProfile || profileForm.invalid"
                      [class.button--loading]="savingProfile"
                      (click)="publishProfile()"
              >
                {{ 'onboarding.publish' | transloco }}
                <i class="fa-solid fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </section>
      }

      <!-- ===== STEP 2: CREATE FIRST SERVICE ===== -->
      @if (currentStep === 2) {
        <section id="onboarding-step2" class="onboarding__content-section">
          <div class="onboarding__content-section-content">
            <h1 class="onboarding__content-title">{{ 'onboarding.step2.title' | transloco }}</h1>
            <p class="onboarding__content-subtitle">{{ 'onboarding.step2.subtitle' | transloco }}</p>

            <!-- SERVICE FORM -->
            <form class="onboarding__content-form" [formGroup]="serviceForm" (ngSubmit)="createFirstService()">
              <div class="input-group">
                <label for="serviceName">{{ 'onboarding.form.service.name' | transloco }}</label>
                <input id="serviceName" type="text" formControlName="serviceName" />
                @if (serviceName.invalid && serviceName.touched) {
                  <small class="error">{{ 'onboarding.errors.service.name' | transloco }}</small>
                }
              </div>

              <div class="input-group">
                <label for="servicePrice">{{ 'onboarding.form.service.price' | transloco }}</label>
                <input id="servicePrice" type="number" inputmode="decimal" min="1" formControlName="servicePrice" />
                @if (servicePrice.invalid && servicePrice.touched) {
                  <small class="error">{{ 'onboarding.errors.service.price' | transloco }}</small>
                }
                <small class="hint">{{ 'onboarding.hints.service.price' | transloco }}</small>
              </div>

              <div class="input-group">
                <label for="serviceDescription">{{ 'onboarding.form.service.description' | transloco }}</label>
                <textarea id="serviceDescription" rows="4" formControlName="serviceDescription"></textarea>
                <small class="hint">{{ 'onboarding.hints.service.description' | transloco }}</small>
              </div>

              <div class="input-group">
                <label for="serviceDuration">{{ 'onboarding.form.service.duration' | transloco }}</label>
                <input id="serviceDuration" type="number" formControlName="serviceDuration" />
                <small class="hint">{{ 'onboarding.hints.service.duration' | transloco }}</small>
              </div>

              <div class="input-group">
                <label>{{ 'onboarding.form.service.image' | transloco }}</label>
                <app-image-upload
                  [buttonSize]="'medium'"
                  [shouldCompress]="false"
                  (imagesSelected)="onServiceImageSelected($event)">
                </app-image-upload>
                <small class="hint">{{ 'onboarding.hints.service.image' | transloco }}</small>
              </div>

              <!-- optional (garde simple pour V1) -->
              <!--
              <div class="input-group input-group--switch">
                <label for="serviceOnline">{{ 'onboarding.form.service.online' | transloco }}</label>
                <input id="serviceOnline" type="checkbox" formControlName="serviceOnline" />
              </div>

              <div class="input-group">
                <label for="serviceAdvantages">{{ 'onboarding.form.service.advantages' | transloco }}</label>
                <app-tags-selector id="serviceAdvantages" formControlName="serviceAdvantages" [max]="6" [position]="'bottom'"></app-tags-selector>
              </div>
              -->
            </form>
          </div>
          <div class="onboarding__content-section-actions">
            <button class="button button--white"
                    (click)="goTo(Step.Profile)"
            >
              <i class="fa-solid fa-arrow-left"></i>
              {{ 'onboarding.step2.back' | transloco }}
            </button>
            <button class="button"
                    type="submit"
                    [disabled]="creatingService || serviceForm.invalid || serviceImageFile === null"
                    [class.button--loading]="creatingService"
                    (click)="createFirstService()"
            >
              {{ 'onboarding.step2.publish' | transloco }}
              <i class="fa-solid fa-check"></i>
            </button>
          </div>
        </section>
      }
    </div>
  </div>
} @else {
  <div class="onboarding__finish"
  >
    <div class="onboarding__finish-checkbox">
      <i class="onboarding__finish-checkbox-check fa-solid fa-circle-check"></i>
    </div>

    <h1 class="onboarding__finish-title">{{ 'onboarding.finish.title' | transloco }}</h1>
    <p class="onboarding__finish-subtitle">{{ 'onboarding.finish.subtitle' | transloco }}</p>

    <a (click)="goToDashboard()"
       class="onboarding__finish-button button"
    >
      {{ 'onboarding.finish.cta' | transloco }}
      <i class="fa-solid fa-arrow-right"></i>
    </a>
  </div>

}


